import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import color from 'color';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { ALERT_BUTTON_BACKGROUND_COLOR, ALERT_BUTTON_BACKGROUND_HOVER_COLOR, ALERT_BUTTON_TEXT_COLOR, AUTO_BET_BUTTON_BACKGROUND_COLOR, AUTO_BET_BUTTON_BACKGROUND_HOVER_COLOR, BUTTON_SUB_LABEL_COLOR, GAME_BUTTON_BACKGROUND_COLOR, GAME_BUTTON_BACKGROUND_HOVER_COLOR, GAME_BUTTON_BORDER_COLOR, SIDE_BET_BUTTON_BACKGROUND_COLOR, SIDE_BET_BUTTON_HOVER_COLOR, SIDE_BET_BUTTON_SUB_LABEL_COLOR, SIDE_BETS_ACCENT_COLOR } from '../styles/colors';
import { isTestEnvironment } from '../utils/utils';
import { ButtonAccentColorDisplay, HEIGHT_RESERVED_FOR_ACCENT_COLOR } from './buttonAccentColorDisplay';
import { BUTTON_BORDER_WIDTH_PX } from './domBetSlider';
const PADDING_X_PX = 5;
const PADDING_Y_PX = 2;
const MAX_LABEL_SCALE = 1.8;
const BASE_FONT_SIZE_PX = 15;
const MAX_ICON_SIZE_PX = 24;
const ICON_GAP_PX = 3;
const BASE_CONTAINER_CSS = css `
  user-select: none;
  -webkit-user-drag: none;
`;
const BASE_LABEL_CSS = css `
  line-height: 80%;
  white-space: nowrap;
  word-spacing: -1px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const LABEL_MEASURE_CSS = css(BASE_LABEL_CSS, css `
    position: absolute;
    opacity: 0;
  `);
const LABEL_TRANSFORM_CONTAINER_CSS = css `
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;
const TRANSPARENT_CSS = css `
  opacity: 0;
`;
function createButtonThemeProperties(opts) {
    return {
        ...opts,
        buttonHighlightColor: color(opts.buttonBackgroundColor).lighten(0.1).hex(),
        buttonHighlightHoverColor: color(opts.buttonBackgroundHoverColor).lighten(0.1).hex()
    };
}
const ALL_BUTTON_THEME_PROPERTIES = {
    bet: createButtonThemeProperties({
        labelColor: '#000000',
        subLabelColor: BUTTON_SUB_LABEL_COLOR,
        buttonBackgroundColor: GAME_BUTTON_BACKGROUND_COLOR,
        borderColor: GAME_BUTTON_BORDER_COLOR,
        buttonBackgroundHoverColor: GAME_BUTTON_BACKGROUND_HOVER_COLOR
    }),
    'auto-bet-off': createButtonThemeProperties({
        labelColor: '#888888',
        subLabelColor: '#888888',
        buttonBackgroundColor: AUTO_BET_BUTTON_BACKGROUND_COLOR,
        borderColor: GAME_BUTTON_BORDER_COLOR,
        buttonBackgroundHoverColor: AUTO_BET_BUTTON_BACKGROUND_HOVER_COLOR
    }),
    'auto-bet-on': createButtonThemeProperties({
        labelColor: ALERT_BUTTON_TEXT_COLOR,
        subLabelColor: ALERT_BUTTON_TEXT_COLOR,
        buttonBackgroundColor: ALERT_BUTTON_BACKGROUND_COLOR,
        borderColor: ALERT_BUTTON_TEXT_COLOR,
        buttonBackgroundHoverColor: ALERT_BUTTON_BACKGROUND_HOVER_COLOR
    }),
    'side-bet': createButtonThemeProperties({
        labelColor: SIDE_BETS_ACCENT_COLOR,
        subLabelColor: SIDE_BET_BUTTON_SUB_LABEL_COLOR,
        buttonBackgroundColor: SIDE_BET_BUTTON_BACKGROUND_COLOR,
        borderColor: SIDE_BETS_ACCENT_COLOR,
        buttonBackgroundHoverColor: SIDE_BET_BUTTON_HOVER_COLOR
    })
};
function getMaxScale(defaultRenderedSize, availableWidth, availableHeight, maxAllowedScale) {
    const isWidthRestricted = defaultRenderedSize.width / defaultRenderedSize.height > availableWidth / availableHeight;
    return Math.min(maxAllowedScale, isWidthRestricted ? availableWidth / defaultRenderedSize.width : availableHeight / defaultRenderedSize.height);
}
function renderSubLabelContents(subLabel, subLabelCss, subLabelTestProps) {
    if (!subLabel) {
        return null;
    }
    return (_jsx("div", { css: subLabelCss, ...subLabelTestProps, children: subLabel }));
}
function renderSingleLineLabelContents(label, subLabel, subLabelCss, labelTestProps, subLabelTestProps) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { ...labelTestProps, children: label }), renderSubLabelContents(subLabel, subLabelCss, subLabelTestProps)] }));
}
function renderMultiLineLabelContents(labelLines, subLabel, subLabelCss, labelTestProps, subLabelTestProps) {
    return (_jsxs(_Fragment, { children: [labelLines.map((labelLine, i) => {
                // apply test props only to the first line of a multi-line label:
                const additionalProps = i === 0 ? labelTestProps : undefined;
                return (_jsx("div", { ...additionalProps, children: labelLine }, i));
            }), renderSubLabelContents(subLabel, subLabelCss, subLabelTestProps)] }));
}
/**
 * Renders a button and optimally scales the button's label, (optional) sub-label and (optional) accent color to fit inside.
 */
export const ScaledTextButton = ({ label, subLabel, testId, onClick, x, y, w, h, accentColor, isXYTopLeft = false, theme = 'bet', startIcon, ariaLabelOverride }) => {
    const singleLineLabelMeasureRef = useRef(null);
    const multiLineLabelMeasureRef = useRef(null);
    const [scaleProperties, setScaleProperties] = useState();
    const showAccentColor = !!(accentColor && h > 60);
    const iconSizePx = useMemo(() => {
        if (!startIcon) {
            return 0;
        }
        return Math.min(Math.min((w - 2 * PADDING_X_PX) / 5, h - 2 * PADDING_Y_PX), MAX_ICON_SIZE_PX);
    }, [h, startIcon, w]);
    useLayoutEffect(() => {
        if (!singleLineLabelMeasureRef.current || !multiLineLabelMeasureRef.current) {
            return;
        }
        const singleLineLabelRect = singleLineLabelMeasureRef.current.getBoundingClientRect();
        const multiLineLabelRect = multiLineLabelMeasureRef.current.getBoundingClientRect();
        const availableW = w - 2 * PADDING_X_PX - (iconSizePx ? Math.max(0, iconSizePx - ICON_GAP_PX) : 0);
        const availableH = h - 2 * PADDING_Y_PX - (showAccentColor ? HEIGHT_RESERVED_FOR_ACCENT_COLOR : 0);
        const singleLineMaxScale = getMaxScale(singleLineLabelRect, availableW, availableH, MAX_LABEL_SCALE);
        const multiLineMaxScale = getMaxScale(multiLineLabelRect, availableW, availableH, MAX_LABEL_SCALE);
        if (singleLineMaxScale >= multiLineMaxScale) {
            setScaleProperties({ scale: singleLineMaxScale, isMultiLineLabel: false });
        }
        else {
            setScaleProperties({ scale: multiLineMaxScale, isMultiLineLabel: true });
        }
    }, [h, iconSizePx, showAccentColor, w, label, subLabel]);
    const { labelColor, subLabelColor, buttonBackgroundColor, borderColor, buttonBackgroundHoverColor, buttonHighlightColor, buttonHighlightHoverColor } = ALL_BUTTON_THEME_PROPERTIES[theme];
    const containerCss = useMemo(() => css(BASE_CONTAINER_CSS, x !== undefined && y !== undefined
        ? css `
              position: absolute;
              left: ${isXYTopLeft ? x : x - w / 2}px;
              top: ${isXYTopLeft ? y : y - h / 2}px;
            `
        : css `
              position: relative;
            `), [h, isXYTopLeft, w, x, y]);
    const buttonBorderRadius = Math.min(Math.min(w, h) / 4, 20);
    const buttonCss = useMemo(() => css(css `
          position: absolute;
          user-select: none;
          border: ${BUTTON_BORDER_WIDTH_PX}px solid ${borderColor};
          background-color: ${buttonBackgroundColor};
          border-radius: ${buttonBorderRadius}px;
          font-size: ${BASE_FONT_SIZE_PX}px;
          width: ${w}px;
          height: ${h}px;
          padding: ${PADDING_Y_PX}px ${PADDING_X_PX}px;
          cursor: pointer;
          &:active {
            border-color: white;
          }
        `, !isMobile &&
        css `
            &:hover {
              background-color: ${buttonBackgroundHoverColor};
              .highlight {
                background-color: ${buttonHighlightHoverColor};
              }
            }
          `), [
        buttonBorderRadius,
        h,
        w,
        buttonBackgroundColor,
        buttonBackgroundHoverColor,
        buttonHighlightHoverColor,
        borderColor
    ]);
    const buttonHighlightPaddingPx = Math.min(w, h) / 20;
    const highlightCss = useMemo(() => css `
      position: absolute;
      width: ${Math.max(0, w - 2 * BUTTON_BORDER_WIDTH_PX - 2 * buttonHighlightPaddingPx)}px;
      height: ${Math.max(0, h / 2 - buttonHighlightPaddingPx)}px;
      left: ${buttonHighlightPaddingPx}px;
      top: ${buttonHighlightPaddingPx}px;
      border-radius: ${Math.max(0, buttonBorderRadius - buttonHighlightPaddingPx)}px;
      background-color: ${buttonHighlightColor};
    `, [buttonBorderRadius, buttonHighlightPaddingPx, h, w, buttonHighlightColor]);
    const iconElement = useMemo(() => startIcon &&
        iconSizePx && (_jsx("div", { css: css `
            position: absolute;
            left: ${PADDING_X_PX}px;
            top: ${h / 2 - iconSizePx / 2}px;
            font-size: ${iconSizePx}px;
            color: ${labelColor};
            pointer-events: none;
          `, children: startIcon })), [h, iconSizePx, labelColor, startIcon]);
    const labelContainerCss = useMemo(() => css `
      position: absolute;
      left: ${PADDING_X_PX + (iconSizePx ? iconSizePx + ICON_GAP_PX : 0)}px;
      top: ${PADDING_Y_PX}px;
      width: ${w - 2 * PADDING_X_PX - (iconSizePx ? iconSizePx + ICON_GAP_PX : 0)}px;
      height: ${h - 2 * PADDING_Y_PX - (showAccentColor ? HEIGHT_RESERVED_FOR_ACCENT_COLOR : 0)}px;
      pointer-events: none;
      user-select: none;
    `, [h, iconSizePx, showAccentColor, w]);
    const ariaLabel = ariaLabelOverride || (subLabel ? `${label} ${subLabel}` : label);
    /**
     * On mobile, trigger the button immediately on pointer down, since there's no easy way to cancel the click
     * if you press on the button and then drag off the button before releasing. onClick will still fire in this
     * case, which is counterintuitive, so it's better UX to just do the action immediately when touched.
     */
    const clickProps = isMobile ? { onPointerDown: onClick } : { onClick };
    const labelLines = label.split(' ');
    const subLabelCss = useMemo(() => css `
      font-size: 80%;
      color: ${subLabelColor};
    `, [subLabelColor]);
    const labelContentsCss = useMemo(() => css(BASE_LABEL_CSS, css `
          color: ${labelColor};
          font-size: ${BASE_FONT_SIZE_PX * (scaleProperties?.scale || 1)}px;
        `, scaleProperties === undefined && TRANSPARENT_CSS), [labelColor, scaleProperties]);
    const labelTestProps = useMemo(() => {
        if (!isTestEnvironment()) {
            return;
        }
        return { 'data-testid': 'button-label', 'data-label': label };
    }, [label]);
    const subLabelTestProps = useMemo(() => {
        if (!isTestEnvironment()) {
            return;
        }
        if (!subLabel) {
            return;
        }
        return { 'data-testid': 'button-sub-label', 'data-label': subLabel };
    }, [subLabel]);
    return (_jsxs("div", { css: containerCss, "data-testid": `${testId}-button-container`, children: [_jsx("div", { ref: singleLineLabelMeasureRef, css: LABEL_MEASURE_CSS, "aria-hidden": true, children: renderSingleLineLabelContents(label, subLabel, subLabelCss) }), _jsx("div", { ref: multiLineLabelMeasureRef, css: LABEL_MEASURE_CSS, "aria-hidden": true, children: renderMultiLineLabelContents(labelLines, subLabel, subLabelCss) }), scaleProperties !== undefined && (_jsxs(_Fragment, { children: [_jsx("div", { ...clickProps, css: buttonCss, "aria-label": ariaLabel, "data-testid": testId, children: _jsx("div", { css: highlightCss, className: 'highlight' }) }), showAccentColor && _jsx(ButtonAccentColorDisplay, { accentColor: accentColor, buttonWidth: w, buttonHeight: h }), iconElement, _jsx("div", { css: labelContainerCss, "aria-hidden": true, children: _jsx("div", { css: LABEL_TRANSFORM_CONTAINER_CSS, children: _jsx("div", { css: labelContentsCss, children: scaleProperties.isMultiLineLabel
                                    ? renderMultiLineLabelContents(labelLines, subLabel, subLabelCss, labelTestProps, subLabelTestProps)
                                    : renderSingleLineLabelContents(label, subLabel, subLabelCss, labelTestProps, subLabelTestProps) }) }) })] }))] }));
};
